import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { navigateToRoot } from 'app/store/common-effects/router.effects';
import { oneRootProject } from 'app/store/root/projects/projects.selectors';
import { assignStatus } from 'app/store/root/users/users.actions';
import { oneUser } from 'app/store/root/users/users.selectors';
import { ProjectOutput } from 'core/http/dev';
import { ProjectStatusOutput, UserOutput } from 'core/http/root';
import { selectFirst, to404PageIfNotFound } from 'core/utils/rx-common';
import { forkJoin, Observable } from 'rxjs';

type AssignedStatus = ProjectStatusOutput & { isAssigned: boolean };

@Component({
  selector: 'app-assigned-statuses',
  templateUrl: './assigned-statuses.component.html',
  styleUrls: ['./assigned-statuses.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AssignedStatusesComponent implements OnInit {
  private _user = new Observable<UserOutput>();
  private _project = new Observable<ProjectOutput>();
  private _assignedStatuses = new Array<AssignedStatus>();

  get project(): Observable<ProjectOutput> {
    return this._project;
  }

  get user(): Observable<UserOutput> {
    return this._user;
  }

  get assignedStatuses(): AssignedStatus[] {
    return this._assignedStatuses;
  }

  constructor(
    private route: ActivatedRoute,
    private store$: Store,
  ) { }

  ngOnInit(): void {
    const managerId = this.route.snapshot.params.managerId;
    const projectId = this.route.snapshot.params.projectId;

    this._user = this.store$.pipe(
      selectFirst(oneUser, { id: managerId }),
      to404PageIfNotFound(this.store$, 'root'),
    );

    this._project = this.store$.pipe(
      selectFirst(oneRootProject, { id: projectId }),
      to404PageIfNotFound(this.store$, 'root'),
    );

    const statuses = this.route.snapshot.data.statuses;
    const assignedStatuses = this.route.snapshot.data.assignedStatuses;

    this._assignedStatuses = statuses.map((status: AssignedStatus) => {
      return {
        ...status,
        isAssigned: assignedStatuses.some((assigned: AssignedStatus) => status.id === assigned.id),
      };
    });
  }

  assignStatuses(): void {
    const statusIds = this.assignedStatuses
      .filter(s => s.isAssigned)
      .map(s => s.id);

    forkJoin({ user: this.user, project: this.project })
      .subscribe(({ user, project }) =>
        this.store$.dispatch(assignStatus({ userId: user.id, projectId: project.id, statusIds })),
      );
  }

  goBack(): void {
    this.store$.dispatch(navigateToRoot({ path: ['managers'] }));
  }
}
